import React, { useMemo } from 'react';

import type { InventoryAgeingResponse, InventoryAgeingRow } from '../../api/inventory';
import { DataSheet, type DataSheetCaptureHandle } from '../ui/DataSheet';

import { DEFAULT_AGEING_EDGES, OVER_THRESHOLD_DAYS, unitsOlderThan } from './inventoryAgeing';
import {
    buildAgeingColumns,
    formatDrr,
    formatReceiptDate,
    formatUnits,
    type AgeingCellTarget,
} from './inventoryAgeingColumns';
import { SharePill } from './AgeingCells';

export const InventoryAgeingTable: React.FC<{
    resp: InventoryAgeingResponse | null;
    /** Rollup rows when a grouping is active; otherwise the response's SKU rows. */
    rows: InventoryAgeingRow[];
    rollupLabel: string | null;
    hiddenColumns: string[];
    dohLabel: string;
    formatDoh: (doh: number | null) => React.ReactNode;
    onCellClick: (target: AgeingCellTarget) => void;
    captureRef?: React.MutableRefObject<DataSheetCaptureHandle | null>;
    byWarehouse?: boolean;
}> = ({ resp, rows, rollupLabel, hiddenColumns, dohLabel, formatDoh, onCellClick, captureRef, byWarehouse = false }) => {
    const edges = resp?.bucket_edges ?? DEFAULT_AGEING_EDGES;
    const hiddenSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns]);

    const columns = useMemo(
        () =>
            buildAgeingColumns({
                edges,
                receiptsFromDate: resp?.receipts_from_date ?? null,
                rollupLabel,
                hiddenSet,
                dohLabel,
                formatDoh,
                onCellClick,
                byWarehouse,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [edges.join(','), resp?.receipts_from_date, rollupLabel, hiddenSet, dohLabel, formatDoh, onCellClick, byWarehouse],
    );

    /* The server's totals describe the full filtered set, which a rollup does not change. */
    const aggregateRow = useMemo(() => {
        const total = resp?.aggregate_row;
        if (!total) return undefined;
        const out: Record<string, React.ReactNode> = { product_id: 'Total' };
        out.on_hand = formatUnits(total.internal.stock);
        total.age.units.forEach((units, index) => {
            out[`age_${index}`] = formatUnits(units);
        });
        out.age_older = formatUnits(total.age.older_than_history);
        const stock = total.internal.stock ?? 0;
        out.over_threshold = stock ? (
            <SharePill share={Math.round((100 * unitsOlderThan(total.age, edges, OVER_THRESHOLD_DAYS)) / stock)} />
        ) : '—';
        out.wtd_age = total.age.weighted_age_days == null ? '—' : `${Math.round(total.age.weighted_age_days)} d`;
        out.oldest = formatReceiptDate(total.age.oldest_receipt_date);
        out.drr = formatDrr(total.internal.drr);
        out.doh = formatDoh(total.internal.doh);
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resp?.aggregate_row, edges.join(','), formatDoh]);

    return (
        <DataSheet
            data={rows}
            columns={columns}
            // A SKU has one row per warehouse in the WAREHOUSE scope.
            getRowKey={(row) => (row.warehouse_id ? `${row.product_id}|${row.warehouse_id}` : row.product_id)}
            getSubRows={(row) => row.subRows}
            aggregateRow={aggregateRow}
            captureRef={captureRef}
            bare
            hideHeaderBar
            maxHeight="auto"
        />
    );
};
